// TUT-450 — clientes (organizações), fusão, regras de fluxo, consultas de CNPJ e CEP, e pessoas com papéis e acesso ao portal.
import { demoApi } from "../demo-data";
import type {
  CnpjLookupResult,
  CepLookupResult,
  CreateOrganizationInput,
  CreatePersonInput,
  OrganizationFlowRule,
  SaveOrganizationFlowRuleInput,
  OrganizationListResponse,
  OrganizationMerge,
  OrganizationMergeImpact,
  OrganizationMergeListResponse,
  PersonListResponse,
  PersonSummary,
  PortalGrantResult,
  PortalRevokeResult,
  RegistryPortalAccess,
  RegistryPortalKind,
  AssignProfessionalRoleInput,
  PersonProfessionalRole,
  ProfessionalRoleKey,
  UpdateProfessionalRoleInput,
  OrganizationSummary,
  UpdateOrganizationInput,
  UpdatePersonInput,
} from "../types";
import { comFatia, request } from "./infra";
import type { FatiaPedida } from "./infra";

// Lido aqui, e não importado: o empacotador só dobra a constante no módulo
// que a escreve, e é isso que tira o demo do build de produção (TUT-448).
const useDemoApi = import.meta.env.VITE_API_MODE === "demo";

export const apiDasOrganizacoesEPessoas = {
  // `updateFeedback` SAIU DAQUI (TUT-085): a triagem do feedback é da Breviti,
  // no console da plataforma. A tela do tenant abre e acompanha.
  /** Ver a nota da fatia opcional em `documents`. */
  organizations(
    search = "",
    fatia: FatiaPedida = {},
  ): Promise<OrganizationListResponse> {
    if (useDemoApi) return demoApi.organizations(search, fatia);
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    // TUT-204c: esta era a ÚNICA das sete listas que montava a fatia à mão, em
    // vez de chamar `comFatia`. A cópia inline não estava errada — ela estava
    // parada: `comFatia` ganhou `sort`/`dir` nesta fatia e Clientes teria sido
    // a única lista a não recebê-los, sem nada na tela dizendo por quê. É a
    // razão de o helper existir desde a TUT-181 fatia 2b.
    return request<OrganizationListResponse>(
      `/organizations${comFatia(params, fatia)}`,
    );
  },
  lookupCnpj(cnpj: string): Promise<CnpjLookupResult> {
    if (useDemoApi) return demoApi.lookupCnpj(cnpj);
    return request<CnpjLookupResult>(
      `/organizations/cnpj/${encodeURIComponent(cnpj)}`,
    );
  },
  lookupCep(cep: string): Promise<CepLookupResult> {
    if (useDemoApi) return demoApi.lookupCep(cep);
    return request<CepLookupResult>(
      `/organizations/cep/${encodeURIComponent(cep)}`,
    );
  },
  createOrganization(
    input: CreateOrganizationInput,
  ): Promise<OrganizationSummary> {
    if (useDemoApi) return demoApi.createOrganization(input);
    return request<OrganizationSummary>("/organizations", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  updateOrganization(
    id: string,
    input: UpdateOrganizationInput,
  ): Promise<OrganizationSummary> {
    if (useDemoApi) return demoApi.updateOrganization(id, input);
    return request<OrganizationSummary>(
      `/organizations/${encodeURIComponent(id)}`,
      {
        method: "PATCH",
        body: JSON.stringify(input),
      },
    );
  },
  // Fusão de organizações (TUT-034): impacto antes, execução depois, e o
  // registro reversível.
  organizationMergeImpact(
    absorbedId: string,
    survivorId: string,
  ): Promise<OrganizationMergeImpact> {
    if (useDemoApi)
      return demoApi.organizationMergeImpact(absorbedId, survivorId);
    return request<OrganizationMergeImpact>(
      `/organizations/${encodeURIComponent(absorbedId)}/merge-impact?into=${encodeURIComponent(survivorId)}`,
    );
  },
  mergeOrganizations(
    absorbedId: string,
    survivorId: string,
  ): Promise<OrganizationMerge> {
    if (useDemoApi) return demoApi.mergeOrganizations(absorbedId, survivorId);
    return request<OrganizationMerge>(
      `/organizations/${encodeURIComponent(absorbedId)}/merge`,
      { method: "POST", body: JSON.stringify({ into: survivorId }) },
    );
  },
  organizationMerges(): Promise<OrganizationMergeListResponse> {
    if (useDemoApi) return demoApi.organizationMerges();
    return request<OrganizationMergeListResponse>("/organizations/merges");
  },
  /**
   * TUT-194 — regra de fluxo por cliente (só QUESTIONS por ora). O `PUT` é
   * idempotente por (escritório, cliente, etapa) — mesmo padrão das
   * referências de viagem da frota.
   */
  organizationFlowRules(): Promise<{ items: OrganizationFlowRule[] }> {
    if (useDemoApi) return demoApi.organizationFlowRules();
    return request<{ items: OrganizationFlowRule[] }>(
      "/organizations/flow-rules",
    );
  },
  saveOrganizationFlowRule(
    input: SaveOrganizationFlowRuleInput,
  ): Promise<OrganizationFlowRule> {
    if (useDemoApi) return demoApi.saveOrganizationFlowRule(input);
    return request<OrganizationFlowRule>("/organizations/flow-rules", {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },
  revertOrganizationMerge(mergeId: string): Promise<OrganizationMerge> {
    if (useDemoApi) return demoApi.revertOrganizationMerge(mergeId);
    return request<OrganizationMerge>(
      `/organizations/merges/${encodeURIComponent(mergeId)}/revert`,
      { method: "POST" },
    );
  },
  people(
    search = "",
    role: ProfessionalRoleKey | "" = "",
    fatia: FatiaPedida = {},
  ): Promise<PersonListResponse> {
    if (useDemoApi) return demoApi.people(search, role, fatia);
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (role) params.set("role", role);
    return request<PersonListResponse>(`/people${comFatia(params, fatia)}`);
  },
  assignPersonRole(
    personId: string,
    input: AssignProfessionalRoleInput,
  ): Promise<PersonProfessionalRole> {
    if (useDemoApi) return demoApi.assignPersonRole(personId, input);
    return request<PersonProfessionalRole>(
      `/people/${encodeURIComponent(personId)}/roles`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },
  updatePersonRole(
    personId: string,
    roleId: string,
    input: UpdateProfessionalRoleInput,
  ): Promise<PersonProfessionalRole> {
    if (useDemoApi) return demoApi.updatePersonRole(personId, roleId, input);
    return request<PersonProfessionalRole>(
      `/people/${encodeURIComponent(personId)}/roles/${encodeURIComponent(roleId)}`,
      { method: "PATCH", body: JSON.stringify(input) },
    );
  },
  createPerson(input: CreatePersonInput): Promise<PersonSummary> {
    if (useDemoApi) return demoApi.createPerson(input);
    return request<PersonSummary>("/people", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  updatePerson(id: string, input: UpdatePersonInput): Promise<PersonSummary> {
    if (useDemoApi) return demoApi.updatePerson(id, input);
    return request<PersonSummary>(`/people/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },
  /**
   * TUT-080 fase 2e — concede (ou devolve) o acesso ao portal desta pessoa.
   *
   * Sem corpo, de propósito: quem manda é o cadastro. O e-mail vem do contato
   * vigente e o alcance vem do vínculo profissional — digitar qualquer um dos
   * dois aqui abriria acesso externo que não corresponde ao diretório.
   */
  grantPortalAccess(id: string): Promise<PortalGrantResult> {
    if (useDemoApi) return demoApi.grantPortalAccess(id);
    return request<PortalGrantResult>(
      `/people/${encodeURIComponent(id)}/portal-access`,
      { method: "POST" },
    );
  },
  /**
   * TUT-540 (D31) — "Dar acesso ao portal" a partir do cadastro de assistente
   * (`experts`) ou de advogado (`counsels`). É a mesma concessão de Pessoas,
   * com a mesma chave (`usuarios.gerir`); a pessoa do diretório nasce no gesto.
   */
  registryPortalAccess(tipo: RegistryPortalKind): Promise<{ items: RegistryPortalAccess[] }> {
    if (useDemoApi) return demoApi.registryPortalAccess(tipo);
    return request<{ items: RegistryPortalAccess[] }>(`/${tipo}/acesso-ao-portal`);
  },
  grantRegistryPortalAccess(tipo: RegistryPortalKind, id: string): Promise<PortalGrantResult> {
    if (useDemoApi) return demoApi.grantRegistryPortalAccess(tipo, id);
    return request<PortalGrantResult>(`/${tipo}/${encodeURIComponent(id)}/portal-access`, { method: "POST" });
  },
  revokeRegistryPortalAccess(tipo: RegistryPortalKind, id: string): Promise<PortalRevokeResult> {
    if (useDemoApi) return demoApi.revokeRegistryPortalAccess(tipo, id);
    return request<PortalRevokeResult>(`/${tipo}/${encodeURIComponent(id)}/portal-access/revoke`, { method: "POST" });
  },
  revokePortalAccess(id: string): Promise<PortalRevokeResult> {
    if (useDemoApi) return demoApi.revokePortalAccess(id);
    return request<PortalRevokeResult>(
      `/people/${encodeURIComponent(id)}/portal-access/revoke`,
      { method: "POST" },
    );
  },
};
